import { ReactSVG } from "react-svg"
import { useRouter } from "next/router"
import dynamic from "next/dynamic"

// ---------------------------------------------------------

import { getLabelIcon, getActivityType } from "../../lib/card"
import RenderBlocks from "../../lib/renderBlocks"
import Layout from "../../layout"

// ---------------------------------------------------------

import Content from "@components/content"
import Grid from "@components/grid"
import PingPong from "@components/pingpong"

const RecommendedReading = dynamic(() => import("@components/recommended-reading"), { ssr: false })
const Speakers = dynamic(() => import("@components/speakers"), { ssr: false })
const TagList = dynamic(() => import("@components/tag-list"), { ssr: false })

// ---------------------------------------------------------

import {
  post_header,
  post_header_icon,
  post_header_label,
  video_template
} from "./styles.module.scss"

// ---------------------------------------------------------

const VideoResourceTemplate = ({ data, recommended, preview }) => {
  const router = useRouter()

  const {
    title,
    excerpt,
    resourceType,
    featuredImage,
    featuredVideo,
    pageContent,
    media,
    video,
    modulesCollection,
    speakersCollection,
    tagCollection,
    isPrivate
  } = data

  const meta = {
    title: title,
    description: excerpt,
    campaign_topics: getActivityType(resourceType),
    robots: isPrivate ? "noindex" : "index"
  }

  const sortedTags = tagCollection?.tags.map((tag) => tag.name)

  // ---------------------------------------------------------

  /**
   *
   * @returns Copy, icons, and date/time details in the hero of the ping pong.
   */
  const getHeader = () => {
    return (
      <header className={post_header}>
        <span className={post_header_label}>
          <ReactSVG className={post_header_icon} src={`/icons/${getLabelIcon(resourceType)}.svg`} />
          {resourceType}
        </span>
        <h1>{title}</h1>
        {tagCollection && <TagList tags={sortedTags} path="/resources" theme="light" />}
      </header>
    )
  }

  /**
   *
   * @returns Featured video or video for the resource, if it exists.
   */
  const getVideo = () => {
    if (featuredVideo?.videoUrl) return featuredVideo
    else if (media?.__typename == "Video") return media
    else if (video?.videoUrl) return video
    else return null
  }

  return (
    <Layout
      preview={preview}
      pageTitle={title}
      featuredImage={featuredImage}
      customMeta={meta}
      navSettings={{
        hideNavLeft: false || router.query._hn,
        hideNavRight: false || router.query._hn
      }}
    >
      <PingPong
        backgroundColor="cement"
        image={{ ...featuredImage }}
        video={getVideo()}
        podcast={media?.__typename === "Podcast" ? media : null}
      >
        {getHeader()}
      </PingPong>

      <Grid className={video_template} column={2} isUneven>
        <Content content={pageContent} data-cy="post-content-container" />
        {speakersCollection?.items?.length > 0 && (
          <Speakers speakersCollection={speakersCollection} />
        )}
      </Grid>

      {modulesCollection?.items.length > 0 && RenderBlocks(modulesCollection.items)}

      <RecommendedReading recommended={recommended} />
    </Layout>
  )
}

export default VideoResourceTemplate
